import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import JobCard from "@/components/JobCard";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Building2, Globe, MapPin, Briefcase, Calendar, Linkedin, Bell, BellRing, MessageSquare } from "lucide-react";
import StarRating from "@/components/StarRating";

export default function PublicCompany() {
  const { cid } = useParams();
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [reviews, setReviews] = useState({ items: [], total: 0, avg_rating: 0, reviews_count: 0 });

  useEffect(() => {
    api.get(`/companies/${cid}`).then(r => setCompany(r.data)).catch(() => setNotFound(true));
    api.get(`/companies/${cid}/jobs`).then(r => setJobs(r.data));
    api.get(`/companies/${cid}/reviews`).then(r => setReviews(r.data)).catch(() => {});
  }, [cid]);

  useEffect(() => {
    if (user?.role === "job_seeker") {
      api.get("/alerts/mine").then(r => setSubscribed(r.data.some(a => a.company_id === cid))).catch(() => {});
    }
  }, [cid, user]);

  const notifyMe = async () => {
    setSubscribing(true);
    try {
      await api.post("/alerts", { company_id: cid, label: `New jobs from ${company.name}` });
      setSubscribed(true);
      toast.success(`You'll be notified about new jobs from ${company.name}`);
    } catch {
      toast.error("Couldn't set up the alert — try again");
    } finally {
      setSubscribing(false);
    }
  };

  if (notFound) return <div className="max-w-4xl mx-auto py-16 text-center text-slate-600">Company not found.</div>;
  if (!company) return <div className="max-w-7xl mx-auto py-10"><div className="skeleton h-44 rounded-xl" /></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="public-company-page">
      <div className="bg-white border border-slate-200 rounded-xl p-8">
        <div className="flex items-start gap-6 flex-wrap">
          {company.logo
            ? <img src={`${process.env.REACT_APP_BACKEND_URL}${company.logo}`} alt="logo" className="w-20 h-20 rounded-xl object-cover border" />
            : <div className="w-20 h-20 rounded-xl bg-[#E8F0FE] grid place-items-center"><Building2 className="w-10 h-10 text-[#1D6FF2]" /></div>}
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans',sans-serif" }}>{company.name}</h1>
              <Badge variant="outline" className="font-mono text-[#1D6FF2] border-[#1D6FF2]">{company.company_id}</Badge>
            </div>
            {company.reviews_count > 0 && (
              <div className="mt-1"><StarRating value={company.avg_rating || 0} count={company.reviews_count} /></div>
            )}
            <p className="text-slate-600 mt-1">{company.industry || "Company"}{company.size ? ` · ${company.size} employees` : ""}</p>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600 mt-3">
              {company.city && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{company.city}</span>}
              {company.founded_year && <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />Founded {company.founded_year}</span>}
              {company.website && <a href={company.website} target="_blank" rel="noreferrer" className="text-[#1D6FF2] hover:underline flex items-center gap-1"><Globe className="w-4 h-4" />Website</a>}
              {company.social_linkedin && <a href={company.social_linkedin} target="_blank" rel="noreferrer" className="text-[#1D6FF2] hover:underline flex items-center gap-1"><Linkedin className="w-4 h-4" />LinkedIn</a>}
              {company.google_profile && <a href={company.google_profile} target="_blank" rel="noreferrer" className="text-[#1D6FF2] hover:underline flex items-center gap-1"><MapPin className="w-4 h-4" />Google Profile</a>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-slate-900">{company.jobs_count}</div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">Open jobs</div>
            {user?.role === "job_seeker" && (
              <button
                type="button"
                onClick={notifyMe}
                disabled={subscribed || subscribing}
                data-testid="notify-me-btn"
                className={`mt-2 text-xs font-medium px-3 py-1.5 rounded-full border flex items-center gap-1 ml-auto transition-colors ${
                  subscribed
                    ? "bg-green-50 text-green-700 border-green-200"
                    : "border-slate-200 text-slate-600 hover:border-black hover:text-black"
                }`}
              >
                {subscribed ? <><BellRing className="w-3 h-3" /> Subscribed</> : <><Bell className="w-3 h-3" /> Notify me</>}
              </button>
            )}
          </div>
        </div>
        {company.description && (
          <div className="mt-6 pt-6 border-t">
            <h3 className="font-semibold mb-2">About</h3>
            <p className="text-slate-700 whitespace-pre-wrap">{company.description}</p>
          </div>
        )}
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4 flex items-center gap-2"><Briefcase className="w-5 h-5" /> Open positions</h2>
      {jobs.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500">No open positions right now.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {jobs.map(j => <JobCard key={j.id} job={j} />)}
        </div>
      )}

      <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4 flex items-center gap-2">
        <MessageSquare className="w-5 h-5" /> Reviews
        {reviews.reviews_count > 0 && <span className="text-base font-normal text-slate-500">({reviews.reviews_count})</span>}
      </h2>
      {reviews.items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500">
          No reviews yet — reviews appear once a hired job seeker rates this company.
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.items.map(r => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <span className="font-medium text-slate-900">{r.user_name}</span>
                  <StarRating value={r.rating} showValue />
                </div>
                <span className="text-xs text-slate-400">{(r.created_at || "").slice(0, 10)}</span>
              </div>
              {r.job_title && <p className="text-xs text-slate-500 mt-1">Hired as {r.job_title}</p>}
              {r.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {r.tags.map(t => (
                    <span key={t} className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{t}</span>
                  ))}
                </div>
              )}
              {r.review_text && <p className="text-sm text-slate-700 mt-3">{r.review_text}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}